import { supabase } from '../lib/supabase';
import { coachService } from './coachService';

const PENDING_PLAYER_STATUSES = ['submitted', 'under_review'];
const PENDING_TEAM_REG_STATUSES = ['submitted', 'under_review'];
const PENDING_COACH_STATUSES = ['submitted', 'under_review'];

const getCurrentUserId = async () => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');
  return user.id;
};

export const approvalService = {
  async getPendingCounts() {
    const [regions, players, teams, regionalTeams, coaches] = await Promise.all([
      supabase
        .from('region_associations')
        .select('*', { count: 'exact', head: true })
        .eq('registration_status', 'pending'),
      supabase
        .from('players')
        .select('*', { count: 'exact', head: true })
        .in('registration_status', PENDING_PLAYER_STATUSES),
      supabase
        .from('teams')
        .select('*', { count: 'exact', head: true })
        .or(
          `team_status.eq.pending_approval,registration_status.in.(${PENDING_TEAM_REG_STATUSES.join(',')})`
        ),
      supabase
        .from('regional_teams')
        .select('*', { count: 'exact', head: true })
        .eq('registration_status', 'pending'),
      supabase
        .from('coaches')
        .select('*', { count: 'exact', head: true })
        .in('registration_status', PENDING_COACH_STATUSES),
    ]);

    const err =
      regions.error || players.error || teams.error || regionalTeams.error || coaches.error;
    if (err) throw new Error(err.message || 'Failed to load pending counts');

    return {
      regions: regions.count || 0,
      players: players.count || 0,
      teams: teams.count || 0,
      regionalTeams: regionalTeams.count || 0,
      coaches: coaches.count || 0,
      total:
        (regions.count || 0) +
        (players.count || 0) +
        (teams.count || 0) +
        (regionalTeams.count || 0) +
        (coaches.count || 0),
    };
  },

  async getPendingRegionAssociations() {
    const { data, error } = await supabase
      .from('region_associations')
      .select('*')
      .eq('registration_status', 'pending')
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load pending regions');

    return (data || []).map((row) => ({
      id: row.id,
      type: 'region',
      title: row.association_name || 'Region Association',
      subtitle: row.region || '—',
      status: row.registration_status,
      email: row.email,
      mobile: row.mobile,
      registrationNumber: row.registration_number,
      createdAt: row.created_at,
      meta: {
        chairperson: row.chairperson_name,
        secretary: row.secretary_name,
      },
    }));
  },

  async getPendingPlayers() {
    const { data, error } = await supabase
      .from('players')
      .select(
        `
        id,
        license_number,
        jersey_number,
        player_position,
        player_status,
        registration_status,
        created_at,
        user_profile:user_profiles!players_user_profile_id_fkey(id, full_name, email, phone),
        team:teams(id, name, short_name)
      `
      )
      .in('registration_status', PENDING_PLAYER_STATUSES)
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load pending players');

    return (data || []).map((row) => ({
      id: row.id,
      type: 'player',
      title: row.user_profile?.full_name || 'Player',
      subtitle: row.team?.name || 'No team',
      status: row.registration_status,
      email: row.user_profile?.email,
      mobile: row.user_profile?.phone,
      licenseNumber: row.license_number,
      createdAt: row.created_at,
      meta: {
        jerseyNumber: row.jersey_number,
        position: row.player_position,
        playerStatus: row.player_status,
      },
    }));
  },

  async getPendingTeams() {
    const { data, error } = await supabase
      .from('teams')
      .select(
        `
        id,
        name,
        short_name,
        home_venue,
        team_status,
        registration_status,
        created_at,
        team_manager:user_profiles!teams_team_manager_id_fkey(id, full_name, email, phone)
      `
      )
      .or(
        `team_status.eq.pending_approval,registration_status.in.(${PENDING_TEAM_REG_STATUSES.join(',')})`
      )
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load pending teams');

    return (data || []).map((row) => ({
      id: row.id,
      type: 'team',
      title: row.name || 'Team',
      subtitle: row.short_name || 'Club team',
      status: row.registration_status || row.team_status,
      email: row.team_manager?.email,
      mobile: row.team_manager?.phone,
      createdAt: row.created_at,
      meta: {
        venue: row.home_venue,
        teamStatus: row.team_status,
        registrationStatus: row.registration_status,
        manager: row.team_manager?.full_name,
      },
    }));
  },

  async getPendingRegionalTeams() {
    const { data, error } = await supabase
      .from('regional_teams')
      .select('*')
      .eq('registration_status', 'pending')
      .order('created_at', { ascending: true });

    if (error) {
      throw new Error(error.message || 'Failed to load pending regional teams');
    }

    return (data || []).map((row) => ({
      id: row.id,
      type: 'regional_team',
      title: row.team_name || 'Regional Team',
      subtitle: 'Regional team registration',
      status: row.registration_status,
      createdAt: row.created_at,
      meta: {
        manager: row.team_manager,
        coach: row.head_coach,
        doctor: row.team_doctor,
      },
    }));
  },

  async getPendingCoaches() {
    const { data, error } = await supabase
      .from('coaches')
      .select('*')
      .in('registration_status', PENDING_COACH_STATUSES)
      .order('created_at', { ascending: true });

    if (error) throw new Error(error.message || 'Failed to load pending coaches');

    return (data || []).map((row) => ({
      id: row.id,
      type: 'coach',
      title: row.coach_name || 'Coach',
      subtitle: `${row.grade || '—'} • ${row.region || '—'}`,
      status: row.registration_status,
      email: row.email,
      mobile: row.mobile,
      licenseNumber: row.license_number,
      createdAt: row.created_at,
      meta: {
        grade: row.grade,
        region: row.region,
        nationality: row.nationality,
        licenseStatus: row.license_status,
        validityPeriod: row.validity_period,
        licenseExpirationDate: row.license_expiration_date,
      },
    }));
  },

  async getAllPending() {
    const [regions, players, teams, regionalTeams, coaches] = await Promise.all([
      this.getPendingRegionAssociations(),
      this.getPendingPlayers(),
      this.getPendingTeams(),
      this.getPendingRegionalTeams(),
      this.getPendingCoaches(),
    ]);

    return { regions, players, teams, regionalTeams, coaches };
  },

  async approveRegion(id) {
    const { data, error } = await supabase
      .from('region_associations')
      .update({
        registration_status: 'approved',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to approve region association');
    return data;
  },

  async rejectRegion(id) {
    const { data, error } = await supabase
      .from('region_associations')
      .update({
        registration_status: 'rejected',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to reject region association');
    return data;
  },

  async approvePlayer(id) {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('players')
      .update({
        registration_status: 'approved',
        player_status: 'active',
        approved_date: new Date().toISOString(),
        approved_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to approve player license');
    return data;
  },

  async rejectPlayer(id) {
    const { data, error } = await supabase
      .from('players')
      .update({
        registration_status: 'rejected',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to reject player license');
    return data;
  },

  async approveTeam(id) {
    const userId = await getCurrentUserId();
    const { data, error } = await supabase
      .from('teams')
      .update({
        registration_status: 'approved',
        team_status: 'active',
        approved_date: new Date().toISOString(),
        approved_by: userId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to approve team registration');
    return data;
  },

  async rejectTeam(id) {
    const { data, error } = await supabase
      .from('teams')
      .update({
        registration_status: 'rejected',
        team_status: 'inactive',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to reject team registration');
    return data;
  },

  async approveRegionalTeam(id) {
    const { data, error } = await supabase
      .from('regional_teams')
      .update({
        registration_status: 'approved',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to approve regional team');
    return data;
  },

  async rejectRegionalTeam(id) {
    const { data, error } = await supabase
      .from('regional_teams')
      .update({
        registration_status: 'rejected',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to reject regional team');
    return data;
  },

  async approveCoach(id) {
    const userId = await getCurrentUserId();
    const basePayload = {
      registration_status: 'approved',
      license_status: 'active',
      updated_at: new Date().toISOString(),
    };

    let result = await supabase
      .from('coaches')
      .update({
        ...basePayload,
        approved_date: new Date().toISOString(),
        approved_by: userId,
      })
      .eq('id', id)
      .select()
      .single();

    if (result.error && /approved_by|approved_date|schema cache/i.test(result.error.message || '')) {
      result = await supabase
        .from('coaches')
        .update(basePayload)
        .eq('id', id)
        .select()
        .single();
    }

    if (result.error) throw new Error(result.error.message || 'Failed to approve coaching license');
    coachService.notifyStatusChange(id, 'approved').catch((err) => {
      console.error('Coach approval notification failed:', err);
    });
    return result.data;
  },

  async rejectCoach(id) {
    const { data, error } = await supabase
      .from('coaches')
      .update({
        registration_status: 'rejected',
        license_status: 'suspended',
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw new Error(error.message || 'Failed to reject coaching license');
    coachService.notifyStatusChange(id, 'rejected').catch((err) => {
      console.error('Coach rejection notification failed:', err);
    });
    return data;
  },

  async decide(item, decision) {
    if (!item?.id || !item?.type) throw new Error('Invalid approval item');
    const approve = decision === 'approve';

    switch (item.type) {
      case 'region':
        return approve ? this.approveRegion(item.id) : this.rejectRegion(item.id);
      case 'player':
        return approve ? this.approvePlayer(item.id) : this.rejectPlayer(item.id);
      case 'team':
        return approve ? this.approveTeam(item.id) : this.rejectTeam(item.id);
      case 'regional_team':
        return approve
          ? this.approveRegionalTeam(item.id)
          : this.rejectRegionalTeam(item.id);
      case 'coach':
        return approve ? this.approveCoach(item.id) : this.rejectCoach(item.id);
      default:
        throw new Error(`Unsupported approval type: ${item.type}`);
    }
  },
};
